import React from 'react';

import { Menu } from '@lumapps/lumx-menu/components/Menu';
import { ButtonProps, DropdownProps } from '@lumapps/lumx/react';

interface CustomizationDropdownProps extends DropdownProps {
    children: React.ReactNode;
    buttonProps?: ButtonProps;
    label: string;
}

/**
 * Wrapper for the DS Dropdown in order to allow the customizations API to use
 * dropdowns as easily as possible. This specifically avoids developers from the customizations API
 * to need the `useRef` hook outside of React.
 * @param CustomizationDropdownProps
 */
const FOCUSABLE_SELECTOR =
    '[role^=menuitem], input, textarea, select, button, [contenteditable], [tabindex]:not([tabindex="-1"])';

// Elements that need focus to work correctly (text entry) — exempt from preventDefault.
const CONTENT_INPUT_SELECTOR = 'input, textarea, select, [contenteditable]';

export const CustomizationDropdown: React.FC<CustomizationDropdownProps> = ({
    children,
    buttonProps = {},
    label,
    closeOnClick,
    ...props
}) => {
    const handleMouseDown = React.useCallback(
        (event: React.MouseEvent) => {
            const target = event.target as HTMLElement;
            // When closeOnClick is false, prevent focus from leaving the trigger for any
            // non-input element. The click event still fires so buttons remain functional;
            // only focus transfer (which triggers the blur-based close) is suppressed.
            if (closeOnClick === false && !target.closest(CONTENT_INPUT_SELECTOR)) {
                event.preventDefault();
                return;
            }
            // Default case: prevent focus going to document.body when clicking non-interactive
            // content (e.g. a Card rendered as a non-focusable article).
            if (!target.closest(FOCUSABLE_SELECTOR)) {
                event.preventDefault();
            }
        },
        [closeOnClick],
    );

    return (
        <Menu.Trigger popoverProps={props}>
            <Menu.TriggerButton {...buttonProps}>{label}</Menu.TriggerButton>
            <Menu onMouseDown={handleMouseDown}>{children}</Menu>
        </Menu.Trigger>
    );
};

CustomizationDropdown.displayName = 'Dropdown';
